import { DEFAULT_LANGUAGE, isLanguage, translate, type Language } from './messages'

/**
 * グローバル言語ストア (in-memory) + localStorage 永続化 + subscribe メカニズム。
 *
 * - 初期言語は localStorage > navigator.language > DEFAULT_LANGUAGE の優先順
 * - `setLanguage` で listener が発火し、`useTranslation` 経由で全コンポーネントが再描画
 * - localStorage は UI 言語設定のみ保存 (秘匿情報ではない)。Cookie は使わない
 */

const LANGUAGE_STORAGE_KEY = 'ookey.language'

let currentLanguage: Language = DEFAULT_LANGUAGE
const listeners = new Set<() => void>()

const detectBrowserLanguage = (): Language => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE
  try {
    const stored = window.localStorage?.getItem(LANGUAGE_STORAGE_KEY)
    if (isLanguage(stored)) return stored
  } catch {
    // localStorage アクセス不可 (private mode 等) は無視
  }

  // 初回訪問時は navigator.languages の優先順リストを走査し ja / en を判定:
  //   - ja-* で始まる locale (ja, ja-JP)        → 'ja'
  //   - en-* で始まる locale (en, en-US, en-GB) → 'en'
  //   - それ以外 (fr / vi / zh / ko 等)          → 'en' (国際 fallback)
  //
  // 旧実装は en 以外を全部 'ja' に fallback していたため、日本語を読まない
  // 海外ユーザーが意図せず日本語画面に当たる問題があった (TASK-LOC-AUTO)。
  const languages =
    window.navigator?.languages && window.navigator.languages.length > 0
      ? window.navigator.languages
      : [window.navigator?.language ?? '']

  for (const lang of languages) {
    const lower = lang.toLowerCase()
    if (lower.startsWith('ja')) return 'ja'
    if (lower.startsWith('en')) return 'en'
  }

  return 'en'
}

currentLanguage = detectBrowserLanguage()

export const getLanguage = (): Language => currentLanguage

export const setLanguage = (next: Language): void => {
  if (currentLanguage === next) return
  currentLanguage = next
  if (typeof window !== 'undefined') {
    try {
      window.localStorage?.setItem(LANGUAGE_STORAGE_KEY, next)
    } catch {
      // 永続化失敗は致命的ではないので握りつぶす
    }
  }
  listeners.forEach(fn => fn())
}

export const subscribe = (fn: () => void): (() => void) => {
  listeners.add(fn)

  return () => {
    listeners.delete(fn)
  }
}

/** 現在の言語で翻訳。React 外でも呼べる (constants 等から)。 */
export const t = (key: string, vars?: Record<string, string | number>): string =>
  translate(currentLanguage, key, vars)
